// Normalização entre o payload do backend e o domínio do front.
//
// NOTA: Cada `||` de default e cada tradução snake_case → camelCase mora AQUI, e só
// aqui. É este arquivo que absorve a divergência no dia em que o backend chegar
// diferente do contrato — sem ele, o ajuste se espalharia por componente.
//
// NOTA: Regra do cálculo local: campo que veio no payload SEMPRE vence. Os utils de
// prioridade e progresso só entram quando o backend omitiu — é assim que a
// promessa "o backend calcula, o front exibe" se sustenta enquanto o mock ainda
// existe.
import { JOURNEY_TEMPLATE_DEFAULT } from '@modules/client-journey/constants/journey-template-default'
import { derivarPrioridade } from '@modules/client-journey/utils/journey-priority/journey-priority'
import {
  etapaAtual,
  progressoDaEsteira,
  progressoDaEtapa,
} from '@modules/client-journey/utils/journey-progress/journey-progress'

const val = (a, b) => (a === undefined || a === null ? b : a)

export function toJourneyTemplate(raw) {
  if (!raw || !Array.isArray(raw.stages) || !raw.stages.length) {
    return JOURNEY_TEMPLATE_DEFAULT
  }
  return {
    id: val(raw.id, JOURNEY_TEMPLATE_DEFAULT.id),
    officeId: val(raw.officeId, val(raw.office_id, null)),
    name: val(raw.name, JOURNEY_TEMPLATE_DEFAULT.name),
    version: val(raw.version, 1),
    editable: !!raw.editable,
    stages: raw.stages.map((stage) => ({
      id: stage.id,
      label: val(stage.label, stage.id),
      shortLabel: val(stage.shortLabel, val(stage.short_label, stage.label)),
      description: val(stage.description, ''),
      order: val(stage.order, 0),
      kind: val(stage.kind, 'unica'),
      slaDays: val(stage.slaDays, val(stage.sla_days, null)),
      weight: val(stage.weight, 1),
      steps: (stage.steps || []).map((step) => ({
        id: step.id,
        label: val(step.label, step.id),
        description: val(step.description, ''),
        order: val(step.order, 0),
        required: step.required !== false,
        actionKey: val(step.actionKey, val(step.action_key, null)),
      })),
    })),
  }
}

function toSignals(raw) {
  const s = raw || {}
  return {
    daysWithoutInteraction: val(
      s.daysWithoutInteraction,
      val(s.days_without_interaction, 0)
    ),
    lastInteractionAt: val(s.lastInteractionAt, val(s.last_interaction_at, null)),
    slaOverdueDays: val(s.slaOverdueDays, val(s.sla_overdue_days, null)),
    patrimonio: val(s.patrimonio, null),
    inEntryPipeline: !!val(s.inEntryPipeline, s.in_entry_pipeline),
  }
}

function toStages(raw) {
  return (Array.isArray(raw) ? raw : []).map((stage) => {
    const steps = (stage.steps || []).map((step) => ({
      stepId: val(step.stepId, val(step.step_id, step.id)),
      status: step.status,
      completedAt: val(step.completedAt, val(step.completed_at, null)),
      blockedReason: val(step.blockedReason, val(step.blocked_reason, null)),
    }))
    const normalizada = {
      stageId: val(stage.stageId, val(stage.stage_id, stage.id)),
      status: stage.status,
      completedAt: val(stage.completedAt, val(stage.completed_at, null)),
      slaDueAt: val(stage.slaDueAt, val(stage.sla_due_at, null)),
      steps,
    }
    normalizada.progressPercent = val(
      stage.progressPercent,
      val(stage.progress_percent, progressoDaEtapa(normalizada))
    )
    return normalizada
  })
}

// Rótulo da etapa que contém o passo. Vive no template, não no payload da
// jornada — quem consome (botão da lista, modal) precisa dele para dizer ONDE o
// passo está, sem ter que carregar o template de novo.
function rotuloDaEtapaDoTemplate(template, stageId) {
  const defStage = ((template && template.stages) || []).find(
    (s) => s.id === stageId
  )
  return (defStage && defStage.label) || ''
}

// Extraído do `toNextAction` para manter os dois dentro do limite de
// complexidade do ESLint (14), que roda como erro no gate.
function nextActionDoPayload(raw, template) {
  const stageId = val(raw.stageId, raw.stage_id)
  return {
    stageId: stageId,
    stepId: val(raw.stepId, raw.step_id),
    label: val(raw.label, ''),
    stageLabel: val(
      raw.stageLabel,
      val(raw.stage_label, rotuloDaEtapaDoTemplate(template, stageId))
    ),
    actionKey: val(raw.actionKey, val(raw.action_key, null)),
    actionLabel: val(raw.actionLabel, val(raw.action_label, null)),
    enabled: raw.enabled !== false,
    disabledReason: val(raw.disabledReason, val(raw.disabled_reason, null)),
  }
}

// Idem: separado de `toNextAction` por causa do limite de complexidade.
function nextActionDerivada(stage, passoAberto, template) {
  const defStage = ((template && template.stages) || []).find(
    (s) => s.id === stage.stageId
  )
  const defStep =
    ((defStage && defStage.steps) || []).find((s) => s.id === passoAberto.stepId) || {}
  return {
    stageId: stage.stageId,
    stepId: passoAberto.stepId,
    label: defStep.label || passoAberto.stepId,
    stageLabel: (defStage && defStage.label) || '',
    actionKey: defStep.actionKey || null,
    // O verbo do botão vem do template, ao lado da chave da ação.
    actionLabel: defStep.actionLabel || null,
    enabled: true,
    disabledReason: null,
  }
}

function toNextAction(raw, template, stages) {
  if (raw) return nextActionDoPayload(raw, template)

  // Derivação local: primeira etapa aberta, primeiro passo não concluído dela.
  const stage = etapaAtual(stages)
  if (!stage) return null
  const passoAberto = (stage.steps || []).find((s) => s.status !== 'concluida')
  if (!passoAberto) return null
  return nextActionDerivada(stage, passoAberto, template)
}

export function toClientJourney(raw, template) {
  if (!raw) return null
  const tpl = template || JOURNEY_TEMPLATE_DEFAULT
  const stages = toStages(raw.stages)
  const signals = toSignals(raw.signals)
  const atual = etapaAtual(stages)
  const currentStageId = val(
    raw.currentStageId,
    val(raw.current_stage_id, atual && atual.stageId)
  )

  return {
    clientId: val(raw.clientId, raw.client_id),
    templateId: val(raw.templateId, val(raw.template_id, tpl.id)),
    templateVersion: val(raw.templateVersion, val(raw.template_version, tpl.version)),
    currentStageId,
    overallPercent: val(
      raw.overallPercent,
      val(raw.overall_percent, progressoDaEsteira(stages, tpl))
    ),
    stages,
    signals,
    priority: raw.priority || derivarPrioridade(signals, currentStageId),
    nextAction: toNextAction(raw.nextAction || raw.next_action, tpl, stages),
    updatedAt: val(raw.updatedAt, val(raw.updated_at, null)),
  }
}

// Já normalizada? O payload de resumo do backend não traz `steps`; o mock traz.
// ATENCAO: a checagem NÃO pode ser "tem steps". O mock (e o payload de jornada
// completa do backend) também têm steps, e o guard antigo devolvia o objeto CRU
// — sem `priority`, sem `nextAction`, sem `currentStageId`. O sintoma em tela:
// os 1135 clientes caíam em "Prioridade baixa" e nenhum botão "Executar"
// renderizava, porque os campos derivados nunca eram calculados.
//
// O sinal correto é `priority`: ele só existe depois da normalização.
function garantirJornadaCompleta(journey, tpl) {
  return journey.priority ? journey : toClientJourney(journey, tpl)
}

function rotuloDaEtapa(defStage, fallback) {
  return (defStage && defStage.label) || fallback || ''
}

// Projeção leve para a listagem — derivada da jornada completa quando o backend
// ainda não expõe o endpoint de resumo.
export function toClientJourneySummary(journey, template) {
  if (!journey) return null
  const tpl = template || JOURNEY_TEMPLATE_DEFAULT
  const completa = garantirJornadaCompleta(journey, tpl)
  if (!completa) return null

  const atual = completa.currentStageId
  const defStage = tpl.stages.find((s) => s.id === atual)
  const stageAtual = (completa.stages || []).find((s) => s.stageId === atual)

  return {
    clientId: completa.clientId,
    currentStageId: atual,
    currentStageLabel: rotuloDaEtapa(defStage, atual),
    stageOrder: (defStage && defStage.order) || 0,
    stagePercent: (stageAtual && stageAtual.progressPercent) || 0,
    priority: completa.priority,
    signals: completa.signals,
    nextAction: completa.nextAction,
  }
}

export function toJourneyIndicators(summaries) {
  const list = Array.isArray(summaries) ? summaries : []
  const porNivel = (nivel) =>
    list.filter((s) => s && s.priority && s.priority.level === nivel).length
  return {
    totalClientes: list.length,
    prioridadeCritica: porNivel('critica'),
    prioridadeAlta: porNivel('alta'),
    esteiraEntrada: list.filter((s) => s && s.signals && s.signals.inEntryPipeline)
      .length,
  }
}

/**
 * Resumo 360 do topo da página de resumo.
 *
 * Devolve SEMPRE o shape completo, com `null` nos campos ausentes: os cards
 * escondem a linha do campo nulo, mas nunca quebram por acessar
 * `overview.patrimonio.total` de um objeto que não veio.
 *
 * @param {Object} raw payload cru
 * @returns {Object|null} ClientJourneyOverview
 */
export function toClientOverview(raw) {
  if (!raw) return null
  const patrimonio = raw.patrimonio || raw.equity || {}
  return {
    clientId: val(raw.clientId, val(raw.client_id, null)),
    perfil: val(raw.perfil, val(raw.suitability_profile, '')),
    objetivo: val(raw.objetivo, val(raw.goal, '')),
    origem: val(raw.origem, val(raw.origin, '')),
    patrimonio: {
      total: val(patrimonio.total, null),
      aporteMensal: val(
        patrimonio.aporteMensal,
        val(patrimonio.monthly_contribution, null)
      ),
      rentabilidade12m: val(
        patrimonio.rentabilidade12m,
        val(patrimonio.return_12m, null)
      ),
    },
    reunioes: (raw.reunioes || raw.meetings || []).map((r) => ({
      tipo: val(r.tipo, val(r.kind, '')),
      data: val(r.data, val(r.date, null)),
      resumo: val(r.resumo, val(r.summary, '')),
    })),
  }
}
